/**
 * Skeleton loading components with CSS shimmer and 200ms minimum display logic
 *
 * Philosophy: Skeleton is a perception tool for smooth UX, not a loading indicator.
 * If data arrives before user perceives loading (< 200ms), skip skeleton entirely.
 */

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import "./SkeletonLoader.css";

interface SkeletonProps {
  /**
   * Whether the skeleton should be shown
   * Internally handles 200ms minimum display logic
   */
  show: boolean;

  /**
   * Number of skeleton items to render (for lists/grids)
   */
  count?: number;
}

/**
 * Hook to handle 200ms minimum skeleton display timing
 * Returns true only if:
 * 1. Component mounted
 * 2. Show prop is true
 * 3. Elapsed time >= 200ms OR data hasn't loaded yet
 */
function useSkeletonTiming(show: boolean): boolean {
  const [shouldShow, setShouldShow] = useState(false);
  const [startTime] = useState(() => Date.now());

  useEffect(() => {
    if (!show) {
      // Use setTimeout to avoid setState in effect warning
      const timer = setTimeout(() => setShouldShow(false), 0);
      return () => clearTimeout(timer);
    }

    const elapsed = Date.now() - startTime;

    if (elapsed >= 200) {
      // Already past minimum time, show immediately
      const timer = setTimeout(() => setShouldShow(true), 0);
      return () => clearTimeout(timer);
    } else {
      // Wait until 200ms has passed
      const timer = setTimeout(() => {
        setShouldShow(true);
      }, 200 - elapsed);

      return () => clearTimeout(timer);
    }
  }, [show, startTime]);

  return shouldShow;
}

/**
 * Skeleton Card - For event cards, gallery items
 */
export function SkeletonCard({ show, count = 1 }: SkeletonProps) {
  const shouldShow = useSkeletonTiming(show);

  if (!shouldShow) return null;

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="skeleton-card"
        >
          <div className="skeleton-image shimmer" />
          <div className="skeleton-card-body">
            <div className="skeleton-text skeleton-text-lg shimmer" />
            <div className="skeleton-text skeleton-text-md shimmer" />
            <div className="skeleton-text skeleton-text-sm shimmer" />
          </div>
        </motion.div>
      ))}
    </>
  );
}

/**
 * Skeleton Row - For table rows
 */
export function SkeletonRow({ show, count = 1 }: SkeletonProps) {
  const shouldShow = useSkeletonTiming(show);

  if (!shouldShow) return null;

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <motion.tr
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <td>
            <div className="skeleton-text skeleton-text-sm shimmer" />
          </td>
          <td>
            <div className="skeleton-text skeleton-text-md shimmer" />
          </td>
          <td>
            <div className="skeleton-text skeleton-text-sm shimmer" />
          </td>
          <td>
            <div className="skeleton-text skeleton-text-xs shimmer" />
          </td>
        </motion.tr>
      ))}
    </>
  );
}

/**
 * Skeleton Image - For gallery grids
 */
export function SkeletonImage({ show, count = 1 }: SkeletonProps) {
  const shouldShow = useSkeletonTiming(show);

  if (!shouldShow) return null;

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="skeleton-image-wrapper"
        >
          <div className="skeleton-image shimmer" />
        </motion.div>
      ))}
    </>
  );
}

/**
 * Skeleton Text - For inline text loading
 */
interface SkeletonTextProps extends SkeletonProps {
  width?: string;
  height?: string;
}

export function SkeletonText({
  show,
  width = "100%",
  height = "1rem",
}: SkeletonTextProps) {
  const shouldShow = useSkeletonTiming(show);

  if (!shouldShow) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="skeleton-text shimmer" style={{ width, height }} />
    </motion.div>
  );
}

/**
 * Skeleton Table - Full table with header and rows
 */
interface SkeletonTableProps extends SkeletonProps {
  columns?: number;
  rows?: number;
}

export function SkeletonTable({
  show,
  columns = 4,
  rows = 8,
}: SkeletonTableProps) {
  const shouldShow = useSkeletonTiming(show);

  if (!shouldShow) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="table-responsive"
    >
      <table className="table table-dark table-hover">
        <thead>
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i}>
                <div className="skeleton-text skeleton-text-sm shimmer" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <SkeletonRow show={true} count={rows} />
        </tbody>
      </table>
    </motion.div>
  );
}

/**
 * Skeleton Event Details - For Karmatek event detail pages
 * Matches the structure of KarmatekEventDetails component
 */
export function SkeletonEventDetails({ show }: SkeletonProps) {
  const shouldShow = useSkeletonTiming(show);

  if (!shouldShow) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="card bg-dark text-white p-4"
      style={{ minHeight: "100vh" }}
    >
      {/* Title skeleton */}
      <div
        className="skeleton-text shimmer mb-3"
        style={{ width: "60%", height: "40px" }}
      />

      {/* Subheading skeleton */}
      <div
        className="skeleton-text shimmer mb-2"
        style={{ width: "40%", height: "20px" }}
      />

      {/* Tagline skeleton */}
      <div
        className="skeleton-text shimmer mb-3"
        style={{ width: "70%", height: "24px" }}
      />

      {/* Badge row skeletons */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="skeleton-text shimmer"
            style={{ width: "80px", height: "24px", borderRadius: "4px" }}
          />
        ))}
      </div>

      {/* Description block skeleton */}
      <div className="mb-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="skeleton-text shimmer mb-2"
            style={{ width: i === 3 ? "60%" : "100%", height: "16px" }}
          />
        ))}
      </div>

      {/* Meta info skeletons (entry fee, prize pool, etc.) */}
      <div className="mb-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="skeleton-text shimmer mb-2"
            style={{ width: "45%", height: "20px" }}
          />
        ))}
      </div>

      {/* Personnel sections skeleton */}
      {Array.from({ length: 2 }).map((_, sectionIdx) => (
        <div key={sectionIdx} className="mt-4">
          <div
            className="skeleton-text shimmer mb-2"
            style={{ width: "120px", height: "24px" }}
          />
          <div className="d-flex flex-wrap gap-2">
            {Array.from({ length: 3 }).map((_, badgeIdx) => (
              <div
                key={badgeIdx}
                className="skeleton-text shimmer"
                style={{ width: "100px", height: "32px", borderRadius: "4px" }}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Rules accordion skeleton */}
      <div className="mt-4">
        <div
          className="skeleton-text shimmer mb-2"
          style={{ width: "100%", height: "48px", borderRadius: "8px" }}
        />
      </div>
    </motion.div>
  );
}
